import React, {useCallback, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {createUserFx} from 'models/profile/staff/table';
import {EmployeeRegistrationRequest} from 'types';
import {ProfileType} from 'types/profile';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type FormField = {
    name: keyof EmployeeRegistrationRequest;
    readonly?: boolean;
    type?: 'text' | 'password';
    options?: SelectOption<string>[];
};

export const CreateUser = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.register.validations.${key}`, replace),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.register.fields.' + key), [
        i18n.language,
    ]);

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                contact_email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .nullable()
                    .notRequired(),
                email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .required(translateValidates('required')),
                password: yup
                    .string()
                    .min(8, translateValidates('password', {length: 8}))
                    .matches(/^(?=.*[A-Za-z])(?=.*\d).*$/, translateValidates('invalid_password'))
                    .required(translateValidates('required')),
                password_confirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .test('password', translateValidates('confirm_password_mismatch'), function(
                        value,
                    ) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        return value === this.parent.password;
                    }),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .required(translateValidates('required'))
                    .typeError(translateValidates('required'))
                    .matches(/^\+?\d{10,14}$/, translateValidates('phone')),
                additional_phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .nullable()
                    .notRequired()
                    .matches(/^\+?\d{10,14}$/, translateValidates('additional_phone')),
                type: yup.mixed<ProfileType.EMPLOYEE>().notRequired(),
                // personal_tin: yup.string().required(translateValidates('required')),
                // .matches(/^\+?\d{14}$/, translateValidates('tin')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setError,
        reset,
    } = useForm<EmployeeRegistrationRequest>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (data: EmployeeRegistrationRequest) => {
        try {
            const payload: EmployeeRegistrationRequest = {
                email: data.email,
                password: data.password,
                type: ProfileType.EMPLOYEE,
                first_name: data.first_name,
                last_name: data.last_name,
                ...(data.contact_email && {contact_email: data.contact_email}),
                ...(data.middle_name && {middle_name: data.middle_name}),
                ...(data.phone && {phone: data.phone}),
                ...(data.additional_phone && {additional_phone: data.additional_phone}),
            };

            await createUserFx(payload);
            reset();
            history.push(ROUTE.STAFF);
        } catch (error) {
            console.error(error);
            const responseData = error.fullErrorsList;
            if (!Array.isArray(responseData)) return;

            responseData.forEach(({field, message}) => setError(field, {type: 'manual', message}));
        }
    };

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'first_name'},
            {name: 'middle_name'},
            {name: 'last_name'},
            {name: 'email'},
            {name: 'password', type: 'password'},
            {name: 'password_confirm', type: 'password'},
            {name: 'contact_email'},
            {name: 'phone'},
            {name: 'additional_phone'},
        ],
        [t],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<EmployeeRegistrationRequest>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
            ...(field.type && {type: field.type}),
            autoComplete: 'new-password',
        };

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    return (
        <>
            <BackTo to={ROUTE.STAFF}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form
                        autoComplete="off"
                        onSubmit={handleSubmit(onSubmit)}
                        style={{marginTop: 50}}
                    >
                        <CustomTitle size={4}>{t('pages.profile.staff.create.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.STAFF);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit">
                                {t('pages.profile.staff.create.createBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
